import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import config from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { FolderOpen, Plus, Link2, Trash2, Pencil, Globe, Power, Ban, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { Workspace } from './Workspace';
import { getDomainBrands, DomainBrand } from '../../utils/branding-api';
import { Workspace as WorkspaceType, activateWorkspace, deactivateWorkspace, deleteWorkspace } from '../../utils/sqrone-api';
import './Workspaces.css';

interface WorkspacesProps {
  embedded?: Boolean;
  onBack?: () => void;
  onViewLinks?: (workspaceId: number) => void;  // FR-26: Callback to navigate to filtered links
}

export function Workspaces({embedded, onBack, onViewLinks} : WorkspacesProps) {
    const { token, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const [workspaces, setWorkspaces] = useState<WorkspaceType[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceType | undefined>(undefined);

    //const [accountBrandings, setAccountBrandings] = useState<AccountBranding[]>([]);
    const [domainBrands, setDomainBrands] = useState<DomainBrand[]>([]);

    const [loading, setLoading] = useState(false);
    const baseUrl = config.apiBaseUrl;

    useEffect(() => {
        if (isAuthenticated && token) {
          fetchWorkspaces();
          //fetchAccountBrandings();
          fetchDomainBrands();
        }
    }, [isAuthenticated, token]);

    useEffect(() => {
      if (!workspaces.length || !domainBrands.length) return;

      // already hydrated? skip
      if (workspaces[0].domainBrand !== undefined) return;

      const domainBrandMap = new Map(
        domainBrands.map(db => [db.id, db])
      );

      const hydratedWorkspaces = workspaces.map(workspace => ({
        ...workspace,
        domainBrand: workspace.domainBrandId
          ? domainBrandMap.get(workspace.domainBrandId) ?? null
          : null,
      }));

      setWorkspaces(hydratedWorkspaces);
    }, [domainBrands]);

    const fetchWorkspaces = async () => {
        try {
          setLoading(true);
          console.log('Fetching workspaces from:', `${baseUrl}/api/workspaces`);
          console.log('Using token:', token ? 'Token present' : 'No token');
          const response = await fetch(`${baseUrl}/api/workspaces`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('Workspaces response status:', response.status);
          if (response.ok) {
            const data = await response.json();
            console.log('Workspaces data:', data);
            setWorkspaces(data);
          } else {
            const error = await response.text();
            console.error('Failed to fetch workspaces:', response.status, error);
          }
        } catch (error) {
          console.error('Error fetching workspaces:', error);
        } finally {
          setLoading(false);
        }
    };

    // const fetchAccountBrandings = async () => {
    //   try {
    //     const response = await fetch(`${baseUrl}/api/branding/logo`, {
    //       headers: { 'Authorization': `Bearer ${token}` }
    //     });
    //     if (response.ok) {
    //       const data = await response.json();
    //       // API returns single branding, convert to array
    //       setAccountBrandings(data.id ? [data] : []);
    //     }
    //   } catch (error) {
    //     console.error('Error fetching brandings:', error);
    //   }
    // };

    const fetchDomainBrands = async () => {
      if (!token) return;

      try {
        const data = await getDomainBrands(token);
        setDomainBrands(data);
      } catch (error) {
        console.error('Error fetching domain brands:', error);
      }
    };



  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setSelectedWorkspace(undefined);
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (workspace: WorkspaceType) => {
    setDialogMode('edit');
    setSelectedWorkspace(workspace);
    setIsDialogOpen(true);
  };

  const handleSuccess = () => {
    setIsDialogOpen(false);
    setSelectedWorkspace(undefined);
    fetchWorkspaces();
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
    setSelectedWorkspace(undefined);
  };

  const handleDeleteWorkspace = async (id: number) => {
    if (!token) return;

    try {
      await deleteWorkspace(id, token);
      toast.success('Workspace deleted');
      await fetchWorkspaces();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete workspace');
      console.error('Delete error:', err);
    }
  };

  // FR-23: Handle workspace activation
  const handleActivate = async (id: number) => {
    if (!token) return;

    try {
      await activateWorkspace(id, token);
      toast.success('Workspace activated (all URLs re-activated)');
      await fetchWorkspaces();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to activate workspace');
      console.error('Activate error:', err);
    }
  };

  // FR-23: Handle workspace deactivation
  const handleDeactivate = async (id: number) => {
    if (!token) return;

    try {
      await deactivateWorkspace(id, token);
      toast.success('Workspace deactivated (all URLs deactivated)');
      await fetchWorkspaces();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to deactivate workspace');
      console.error('Deactivate error:', err);
    }
  };

  // FR-26: Handle viewing links for a workspace
  const handleViewLinks = (workspaceId: number) => {
    if (embedded && onViewLinks) {
      // When embedded in ShortLinks, use the callback
      onViewLinks(workspaceId);
    } else {
      // When standalone, navigate to manage page with workspace filter
      navigate(`/manage?workspace=${workspaceId}`);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  if (!isAuthenticated || !token) {
    return (
    <div className="workspaces-container">
        <div className="no-selection">
        <p>Please sign in to manage workspaces</p>
        </div>
    </div>
    );
}

  return (
    <div className="workspaces">
    <div className="workspaces-content">
    <div className="space-y-6">
      {/* Header */}
      <div className="workspaces-header">
      { embedded && (
        <Button variant="ghost" onClick={onBack} className="mb-2">
          ← Back to URLs
        </Button>)
      }
        <div>
          <p className="text-gray-600 mt-1">Organize your shortened URLs into workspaces</p>
        </div>
        <Button
          onClick={handleOpenCreateDialog}
          className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Workspace
        </Button>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0">
            <DialogHeader className="px-6 pt-6 pb-4">
              <DialogTitle>
                {dialogMode === 'create' ? 'Create New Workspace' : 'Edit Workspace'}
              </DialogTitle>
              <DialogDescription>
                {dialogMode === 'create'
                  ? 'Create a new workspace to organize your shortened URLs with custom branding and settings.'
                  : 'Edit workspace details, branding, geo-targeting, and schedule settings.'}
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto px-6 pb-6 flex-1">
              <Workspace
                mode={dialogMode}
                workspace={selectedWorkspace}
                domains={domainBrands}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Workspaces Grid */}
      {loading ? (
              <p>Loading...</p>
            ) : workspaces.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workspaces.map((workspace) => (
            <Card key={workspace.id} className="shadow-md border-0 bg-white/80 backdrop-blur hover:shadow-lg transition-shadow flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FolderOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{workspace.name}</CardTitle>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEditDialog(workspace)}
                      className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteWorkspace(workspace.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 flex-grow flex flex-col">
                {/* Description - always reserve space for consistent card height */}
                <div className="min-h-[2.5rem]">
                  {workspace.description && (
                    <p className="text-sm text-gray-600">{workspace.description}</p>
                  )}
                </div>

                {/* FR-28: Always show domain - branded or default */}
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-600">Domain:</span>
                  {workspace.domainBrand ? (
                    <>
                      <span className="font-mono text-blue-600">{workspace.domainBrand.domain}</span>
                      {workspace.domainBrand.verified && (
                        <span className="text-xs text-green-600">✓ Verified</span>
                      )}
                    </>
                  ) : (
                    <span className="font-mono text-gray-500">
                      {config.apiBaseUrl.replace('http://', '').replace('https://', '')}
                    </span>
                  )}
                </div>

                {/* FR-23: Activation Status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={workspace.isActive !== false ? "default" : "destructive"}
                      className="text-xs flex items-center gap-1"
                    >
                      {workspace.isActive !== false ? (
                        <>
                          <Power className="size-3" />
                          Active
                        </>
                      ) : (
                        <>
                          <Ban className="size-3" />
                          Inactive
                        </>
                      )}
                    </Badge>
                    {workspace.isActive !== false && workspace.allowedCountries && (
                      <Badge variant="outline" className="text-xs">
                        Geo-Restricted
                      </Badge>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={workspace.isActive !== false ? "outline" : "default"}
                    onClick={(e) => {
                      e.stopPropagation();
                      workspace.isActive !== false ? handleDeactivate(workspace.id) : handleActivate(workspace.id);
                    }}
                    className="text-xs h-7"
                  >
                    {workspace.isActive !== false ? (
                      <>
                        <Ban className="size-3 mr-1" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <Power className="size-3 mr-1" />
                        Activate
                      </>
                    )}
                  </Button>
                </div>

                {/* Spacer to push remaining content to bottom */}
                <div className="flex-grow"></div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1 text-gray-600">
                    <Link2 className="w-4 h-4" />
                    <span>{workspace.tags} links</span>
                  </div>
                  <span className="text-gray-500">{formatDate(workspace.createdAt)}</span>
                </div>

                {/* FR-26: View Links Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleViewLinks(workspace.id)}
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Links
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="shadow-md border-0 bg-white/80 backdrop-blur">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <FolderOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-gray-900 mb-2">No workspaces yet</h3>
            <p className="text-gray-600 mb-6">
              Create workspaces to organize your shortened URLs
            </p>
            <Button
              onClick={handleOpenCreateDialog}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Workspace
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
    </div>
    </div>
  );
}
